/**
 * Rename modal for an audience — edits the title only (audiences have no description). Opened from the
 * detail page's ⋯ menu; persists on Save and closes only once the rename succeeds, staying open for a
 * retry on failure. Mirrors the workflow `RenameRoutineDialog` so the two rename surfaces read alike.
 */
import { useState } from 'react';

import { Button } from '@/_shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/_shared/components/ui/dialog';
import { Input } from '@/_shared/components/ui/input';
import { Label } from '@/_shared/components/ui/label';

/** Matches the server Zod cap on the audience title (`rename` handler) — never silently truncate below it. */
const AUDIENCE_TITLE_MAX = 500;

interface RenameAudienceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Current title — seeds the field each time the modal opens. */
  title: string;
  /** True while the rename API call is in flight — locks the dialog and drives the Save spinner. */
  saving: boolean;
  /** Persists the rename; resolves on success (the dialog closes), rejects on failure (it stays open). */
  onSave: (title: string) => Promise<void>;
}

export function RenameAudienceDialog({
  open,
  onOpenChange,
  title,
  saving,
  onSave,
}: RenameAudienceDialogProps): React.ReactElement {
  return (
    // Locked while the rename is in flight — ignore outside-click / Escape so it can't be dismissed mid-save.
    <Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename audience</DialogTitle>
          <DialogDescription>Update the audience&apos;s name.</DialogDescription>
        </DialogHeader>
        {/* The form mounts fresh on each open (Radix unmounts closed content), so its field seeds from
            the current title without a set-state-in-effect. */}
        <RenameForm
          title={title}
          saving={saving}
          onCancel={() => onOpenChange(false)}
          onSave={async (next) => {
            // Wait for the API call to succeed before closing; on failure keep the dialog open (the
            // caller already surfaced a toast).
            try {
              await onSave(next);
              onOpenChange(false);
            } catch {
              /* stay open for a retry */
            }
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

interface RenameFormProps {
  title: string;
  saving: boolean;
  onCancel: () => void;
  onSave: (title: string) => Promise<void>;
}

function RenameForm({ title, saving, onCancel, onSave }: RenameFormProps): React.ReactElement {
  const [value, setValue] = useState(title);
  const trimmed = value.trim();
  const disabled = saving || trimmed.length === 0;

  const submit = (): void => {
    if (trimmed.length === 0) return;
    void onSave(trimmed.slice(0, AUDIENCE_TITLE_MAX));
  };

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="audience-rename-name">Name</Label>
        <Input
          id="audience-rename-name"
          value={value}
          onChange={(e) => setValue(e.target.value.slice(0, AUDIENCE_TITLE_MAX))}
          maxLength={AUDIENCE_TITLE_MAX}
          placeholder="Untitled audience"
          disabled={saving}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !disabled) {
              e.preventDefault();
              submit();
            }
          }}
        />
      </div>
      <DialogFooter>
        <Button variant="secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        {/* Save persists via the API; the dialog stays open (spinner) until it succeeds. Disabled on an
            empty name (the server requires min 1). */}
        <Button variant="primary" loading={saving} disabled={disabled} onClick={submit}>
          Save
        </Button>
      </DialogFooter>
    </>
  );
}
